/**
 * Incremental `text/event-stream` frame parser (CONTRACTS §5).
 *
 * Chunk-split safe: network chunks cut fields, lines and UTF-8 sequences at
 * arbitrary offsets, so the parser holds a residual buffer and only emits a
 * frame on a blank line. Comment lines (`: hb`) are the 15 s keepalive and
 * produce no frame but DO count as traffic — `push()` reports them so the
 * client can distinguish "quiet" from "dead".
 */

export interface SseFrame {
  /** `<gen>-<seq>`; null when the server omitted `id:`. */
  readonly id: string | null;
  readonly event: string;
  readonly data: string;
}

export interface SseParseResult {
  readonly frames: SseFrame[];
  /** `retry:` value seen in this chunk, if any. */
  readonly retryMs: number | null;
  /** True when the chunk contained a keepalive comment. */
  readonly sawComment: boolean;
}

export class SseFrameParser {
  private buffer = '';
  private dataLines: string[] = [];
  private eventName: string | null = null;
  private lastId: string | null = null;
  private frameId: string | null = null;
  private frameHasId = false;

  /** The most recent `id:` seen — what a reconnect must send as Last-Event-ID. */
  get lastEventId(): string | null {
    return this.lastId;
  }

  reset(): void {
    this.buffer = '';
    this.dataLines = [];
    this.eventName = null;
    this.frameId = null;
    this.frameHasId = false;
  }

  push(chunk: string): SseParseResult {
    this.buffer += chunk;
    const frames: SseFrame[] = [];
    let retryMs: number | null = null;
    let sawComment = false;

    // Normalise the three legal line terminators, then split on \n.
    this.buffer = this.buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    let newlineIdx = this.buffer.indexOf('\n');
    while (newlineIdx !== -1) {
      const line = this.buffer.slice(0, newlineIdx);
      this.buffer = this.buffer.slice(newlineIdx + 1);
      newlineIdx = this.buffer.indexOf('\n');

      if (line === '') {
        const frame = this.dispatch();
        if (frame) frames.push(frame);
        continue;
      }
      if (line.startsWith(':')) {
        sawComment = true;
        continue;
      }

      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      let value = colon === -1 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);

      switch (field) {
        case 'event':
          this.eventName = value;
          break;
        case 'data':
          this.dataLines.push(value);
          break;
        case 'id':
          // Per the spec an id containing NUL is ignored entirely.
          if (!value.includes(' ')) {
            this.frameId = value;
            this.frameHasId = true;
          }
          break;
        case 'retry': {
          const parsed = Number(value);
          if (Number.isInteger(parsed) && parsed >= 0) retryMs = parsed;
          break;
        }
        default:
          break;
      }
    }

    return { frames, retryMs, sawComment };
  }

  private dispatch(): SseFrame | null {
    if (this.frameHasId) {
      this.lastId = this.frameId;
    }
    if (this.dataLines.length === 0) {
      this.eventName = null;
      this.frameId = null;
      this.frameHasId = false;
      return null;
    }
    const frame: SseFrame = {
      id: this.frameHasId ? this.frameId : this.lastId,
      event: this.eventName ?? 'message',
      data: this.dataLines.join('\n'),
    };
    this.dataLines = [];
    this.eventName = null;
    this.frameId = null;
    this.frameHasId = false;
    return frame;
  }
}
